import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import useReservations from "../hooks/useReservations";
import API from "../api/api";
import { downloadRentalDossierPdf } from "../api/reservations";
import { ensureAdminSettlement, getAdminSettlements } from "../api/adminSettlements";
import { contactErrors } from "../utils/contactValidation";
import toast from "react-hot-toast";

const statusLabels = {
  PENDING: "In afwachting",
  APPROVED: "Goedgekeurd",
  REJECTED: "Afgekeurd",
  CHECKED_OUT: "Uitgecheckt",
  COMPLETED: "Afgerond"
};

const depositLabels = {
  UNPAID: "Niet betaald",
  PAID: "Betaald",
  REFUNDED: "Terugbetaald",
  PARTIAL_REFUND: "Gedeeltelijk terugbetaald",
  NO_REFUND: "Geen terugbetaling"
};

const reservationBadge = (status) => {
  if (status === "APPROVED" || status === "COMPLETED" || status === "CHECKED_OUT") return "success";
  if (status === "REJECTED") return "danger";
  return "warning";
};

const depositBadge = (status) => {
  if (status === "PAID" || status === "REFUNDED") return "success";
  if (status === "NO_REFUND") return "danger";
  if (status === "PARTIAL_REFUND") return "info";
  return "warning";
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("nl-BE");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("nl-BE");
};

const formatMoney = (value) => new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR"
}).format(Number(value || 0));

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseInputDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const todayInput = () => toDateInput(new Date());

const rangesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

const monthLabel = (date) => (
  date.toLocaleDateString("nl-BE", { month: "long", year: "numeric" })
);

function AvailabilityCalendar({
  monthDate,
  reservations,
  selectedStartDate,
  selectedEndDate,
  activeDateField,
  onSelectDate,
  onPrevious,
  onNext
}) {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7;
  const cells = [];

  for (let i = 0; i < offset; i += 1) {
    cells.push({ key: `empty-${i}`, empty: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const past = date < parseInputDate(todayInput());
    const unavailable = reservations.some((reservation) => (
      rangesOverlap(
        date,
        date,
        new Date(reservation.startDate),
        new Date(reservation.endDate)
      )
    ));

    cells.push({
      key: toDateInput(date),
      day,
      unavailable,
      past
    });
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button className="button secondary" onClick={onPrevious}>Vorige</button>
        <div className="calendar-title">{monthLabel(monthDate)}</div>
        <button className="button secondary" onClick={onNext}>Volgende</button>
      </div>

      <div className="calendar-grid">
        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((day) => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}

        {cells.map((cell) => (
          <button
            key={cell.key}
            type="button"
            disabled={cell.empty || cell.unavailable || cell.past}
            className={`calendar-day${cell.empty ? " muted" : ""}${cell.unavailable || cell.past ? " unavailable" : ""}${cell.key === selectedStartDate || cell.key === selectedEndDate ? " selected" : ""}`}
            onClick={() => onSelectDate(cell.key)}
            title={cell.past ? "Datum ligt in het verleden" : cell.unavailable ? "Deze datum is bezet" : `Gebruik als ${activeDateField === "endDate" ? "einddatum" : "startdatum"}`}
          >
            {cell.empty ? "" : cell.day}
          </button>
        ))}
      </div>

      <div className="calendar-legend">
        <span className="badge success">Lichtgroen = beschikbaar</span>
        <span className="badge danger">Lichtrood = bezet</span>
      </div>
    </div>
  );
}

export default function Reservations() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data = [], isLoading } = useReservations();
  const queryClient = useQueryClient();
  const today = todayInput();
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [activeDateField, setActiveDateField] = useState("startDate");
  const [editForm, setEditForm] = useState({
    firstName: "",
    name: "",
    email: "",
    phone: "",
    trailerId: "",
    startDate: "",
    endDate: ""
  });

  const [form, setForm] = useState({
    customerId: "",
    firstName: "",
    name: "",
    address: "",
    phone: "",
    email: "",
    trailerId: "",
    startDate: "",
    endDate: ""
  });
  const editValidationErrors = contactErrors(editForm.phone, editForm.email);

  const { data: trailers = [] } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => {
      const res = await API.get("/trailers");
      return res.data;
    }
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const res = await API.get("/customers");
      return res.data;
    }
  });

  const { data: settlements = [] } = useQuery({
    queryKey: ["admin", "settlements"],
    queryFn: getAdminSettlements
  });

  const filteredReservations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;

    return data.filter((reservation) => {
      const values = [
        reservation.reservationCode,
        reservation.id,
        reservation.firstName,
        reservation.name,
        reservation.customer?.firstName,
        reservation.customer?.lastName,
        reservation.email,
        reservation.customer?.email,
        reservation.phone,
        reservation.customer?.phone
      ];

      return values.some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [data, search]);

  const selectedReservation = useMemo(
    () => data.find((reservation) => reservation.id === selectedId) || filteredReservations[0] || data[0],
    [data, filteredReservations, selectedId]
  );
  const selectedSettlement = selectedReservation
    ? settlements.find((settlement) => settlement.reservationId === selectedReservation.id)
    : null;
  const checkoutLink = selectedReservation
    ? `${window.location.origin}/checkout/${selectedReservation.id}`
    : "";

  const selectedTrailerReservations = useMemo(() => {
    if (!form.trailerId) return [];

    return data.filter((reservation) => (
      reservation.trailerId === Number(form.trailerId) &&
      ["PENDING", "APPROVED", "CHECKED_OUT"].includes(reservation.status)
    ));
  }, [data, form.trailerId]);

  const hasDateConflict = useMemo(() => {
    const start = parseInputDate(form.startDate);
    const end = parseInputDate(form.endDate);

    if (!form.trailerId || !start || !end || start > end) return false;

    return selectedTrailerReservations.some((reservation) => (
      rangesOverlap(start, end, new Date(reservation.startDate), new Date(reservation.endDate))
    ));
  }, [form, selectedTrailerReservations]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservationId = Number(params.get("reservationId"));

    if (reservationId && data.some((reservation) => reservation.id === reservationId)) {
      setSelectedId(reservationId);
      return;
    }

    if (!filteredReservations.length) {
      setSelectedId(null);
      return;
    }

    if (!selectedId || !filteredReservations.some((reservation) => reservation.id === selectedId)) {
      setSelectedId(filteredReservations[0].id);
    }
  }, [data, filteredReservations, selectedId, location.search]);

  useEffect(() => {
    if (!selectedReservation) {
      setEditForm({
        firstName: "",
        name: "",
        email: "",
        phone: "",
        trailerId: "",
        startDate: "",
        endDate: ""
      });
      return;
    }

    setEditForm({
      firstName: selectedReservation.firstName || "",
      name: selectedReservation.name || "",
      email: selectedReservation.email || "",
      phone: selectedReservation.phone || "",
      trailerId: selectedReservation.trailerId ? String(selectedReservation.trailerId) : "",
      startDate: selectedReservation.startDate?.slice(0, 10) || "",
      endDate: selectedReservation.endDate?.slice(0, 10) || ""
    });
  }, [selectedReservation]);

  const refreshReservations = () => {
    queryClient.invalidateQueries({ queryKey: ["reservations"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const handleMutationError = (error, fallback) => {
    refreshReservations();
    toast.error(error.response?.data?.error || fallback);
  };

  const createMutation = useMutation({
    mutationFn: (newData) => API.post("/reservations", newData),
    onSuccess: () => {
      refreshReservations();
      toast.success("Reservatie aangemaakt");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Reservatie aanmaken mislukt");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => API.delete(`/reservations/${id}`),
    onSuccess: () => {
      refreshReservations();
      toast.success("Reservatie verwijderd");
    },
    onError: () => {
      toast.error("Verwijderen mislukt");
    }
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => API.patch(`/reservations/${id}`, { status }),
    onSuccess: () => {
      refreshReservations();
      toast.success("Status bijgewerkt");
    },
    onError: (error) => handleMutationError(error, "Status bijwerken mislukt")
  });

  const depositMutation = useMutation({
    mutationFn: ({ id, depositStatus }) => API.patch(`/reservations/${id}`, { depositStatus }),
    onSuccess: () => {
      refreshReservations();
      toast.success("Waarborgstatus bijgewerkt");
    },
    onError: (error) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Log opnieuw in als admin om de waarborgstatus te wijzigen");
        return;
      }

      handleMutationError(error, "Waarborgstatus bijwerken mislukt");
    }
  });

  const settlementMutation = useMutation({
    mutationFn: ensureAdminSettlement,
    onSuccess: (res) => {
      refreshReservations();
      queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
      toast.success("Waarborg staat klaar voor afhandeling");
      navigate(`/admin/settlements/${res.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Waarborg klaarzetten mislukt");
    }
  });

  const updateReservationStatus = (status) => {
    if (!selectedReservation) return;
    statusMutation.mutate({ id: selectedReservation.id, status });
  };

  const updateDepositStatus = (depositStatus) => {
    if (!selectedReservation) return;
    depositMutation.mutate({ id: selectedReservation.id, depositStatus });
  };

  const updateReservationMutation = useMutation({
    mutationFn: ({ id, payload }) => API.patch(`/reservations/${id}`, payload),
    onSuccess: () => {
      refreshReservations();
      toast.success("Reservatiegegevens opgeslagen");
    },
    onError: (error) => handleMutationError(error, "Reservatie opslaan mislukt")
  });

  const downloadDossierMutation = useMutation({
    mutationFn: downloadRentalDossierPdf,
    onSuccess: (blob, reservationId) => {
      const reservation = data.find((item) => item.id === reservationId);
      const filename = `verhuurdossier-${reservation?.reservationCode || reservationId}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Verhuurdossier gedownload");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Verhuurdossier downloaden mislukt");
    }
  });

  const selectCustomerForReservation = (customerId) => {
    const customer = customers.find((item) => item.id === Number(customerId));
    setForm({
      ...form,
      customerId,
      firstName: customer?.firstName || "",
      name: customer?.lastName || "",
      address: customer?.address || "",
      phone: customer?.phone || "",
      email: customer?.email || ""
    });
  };

  const createReservation = () => {
    const start = parseInputDate(form.startDate);
    const end = parseInputDate(form.endDate);
    const todayDate = parseInputDate(today);

    if (!form.customerId) {
      toast.error("Selecteer eerst een bestaande klant");
      return;
    }

    if (!form.trailerId || !form.startDate || !form.endDate) {
      toast.error("Klant, trailer, startdatum en einddatum zijn verplicht");
      return;
    }

    if (start && todayDate && start < todayDate) {
      toast.error("Startdatum mag niet in het verleden liggen.");
      return;
    }

    if (start && end && end < start) {
      toast.error("Einddatum mag niet vóór de startdatum liggen.");
      return;
    }

    if (hasDateConflict) {
      toast.error("Deze trailer is niet beschikbaar in de gekozen periode");
      return;
    }

    createMutation.mutate(form);
  };

  const selectCalendarDate = (dateValue) => {
    if (!dateValue) return;

    if (activeDateField === "endDate") {
      const nextEnd = parseInputDate(dateValue);
      const currentStart = parseInputDate(form.startDate);

      if (currentStart && nextEnd && nextEnd < currentStart) {
        toast.error("Einddatum mag niet vóór de startdatum liggen.");
        return;
      }
    }

    const nextForm = {
      ...form,
      [activeDateField]: dateValue
    };

    if (activeDateField === "startDate") {
      const currentEnd = parseInputDate(form.endDate);
      const nextStart = parseInputDate(dateValue);

      if (!currentEnd || nextStart > currentEnd) {
        nextForm.endDate = "";
      }

      setActiveDateField("endDate");
    }

    setForm(nextForm);
  };

  const updateCreateStartDate = (value) => {
    const nextForm = { ...form, startDate: value };
    const nextStart = parseInputDate(value);
    const currentEnd = parseInputDate(form.endDate);

    if (nextStart && currentEnd && currentEnd < nextStart) {
      nextForm.endDate = "";
    }

    setForm(nextForm);
  };

  const updateEditStartDate = (value) => {
    const nextForm = { ...editForm, startDate: value };
    const nextStart = parseInputDate(value);
    const currentEnd = parseInputDate(editForm.endDate);

    if (nextStart && currentEnd && currentEnd < nextStart) {
      nextForm.endDate = "";
    }

    setEditForm(nextForm);
  };

  const saveEditReservation = () => {
    const start = parseInputDate(editForm.startDate);
    const end = parseInputDate(editForm.endDate);

    if (editValidationErrors.phone || editValidationErrors.email) {
      toast.error(editValidationErrors.phone || editValidationErrors.email);
      return;
    }

    if (start && end && end < start) {
      toast.error("Einddatum mag niet vóór de startdatum liggen.");
      return;
    }

    updateReservationMutation.mutate({
      id: selectedReservation.id,
      payload: editForm
    });
  };

  if (isLoading) return <div className="loading-state">Reservaties laden...</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Reservaties</h1>
          <p className="page-subtitle">Bekijk boekingen, keur ze manueel goed en volg betaling en waarborg op.</p>
        </div>
      </header>

      <div className="panel panel-pad">
        <p className="page-subtitle" style={{ marginTop: 0 }}>
          Eerst een nieuwe klant? Maak deze eerst aan via Klanten en keer daarna terug naar Reservaties.
        </p>
        <div className="form-grid">
          <label className="field">
            <span className="field-label">Bestaande klant</span>
            <select
              className="select"
              value={form.customerId}
              onChange={(e) => selectCustomerForReservation(e.target.value)}
            >
              <option value="">Zoek/selecteer bestaande klant</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.firstName} {customer.lastName} - {customer.phone || customer.email || "geen contact"}
                </option>
              ))}
            </select>
          </label>

          <Link className="button secondary" to="/customers">
            Nieuwe klant aanmaken
          </Link>

          <label className="field">
            <span className="field-label">Trailer</span>
            <select
              className="select"
              value={form.trailerId}
              onChange={(e) =>
                setForm({ ...form, trailerId: e.target.value })
              }
            >
              <option value="">Selecteer trailer</option>
              {trailers.map((trailer) => (
                <option key={trailer.id} value={trailer.id}>
                  {trailer.name} - MTM {trailer.mtm}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">Startdatum</span>
            <input
              className="input"
              type="date"
              min={today}
              value={form.startDate}
              onFocus={() => setActiveDateField("startDate")}
              onChange={(e) =>
                updateCreateStartDate(e.target.value)
              }
            />
          </label>

          <label className="field">
            <span className="field-label">Einddatum</span>
            <input
              className="input"
              type="date"
              min={form.startDate || today}
              value={form.endDate}
              onFocus={() => setActiveDateField("endDate")}
              onChange={(e) =>
                setForm({ ...form, endDate: e.target.value })
              }
            />
          </label>

          <button
            className="button"
            onClick={createReservation}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Aanmaken..." : "Aanmaken"}
          </button>
        </div>

        {form.customerId && (
          <div className="detail-card" style={{ marginTop: 12 }}>
            <div className="detail-label">Geselecteerde klant</div>
            <div className="detail-value">
              {form.firstName} {form.name} - {form.phone || form.email || "geen contactgegevens"}
            </div>
          </div>
        )}

        {hasDateConflict && (
          <p className="error-text">Deze trailer is al geboekt in deze periode.</p>
        )}
      </div>

      {form.trailerId && (
        <div className="panel panel-pad" style={{ marginTop: 18 }}>
          <AvailabilityCalendar
            monthDate={calendarMonth}
            reservations={selectedTrailerReservations}
            selectedStartDate={form.startDate}
            selectedEndDate={form.endDate}
            activeDateField={activeDateField}
            onSelectDate={selectCalendarDate}
            onPrevious={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
            onNext={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
          />
        </div>
      )}

      <div className="panel panel-pad" style={{ marginTop: 18 }}>
        <input
          className="input"
          placeholder="Zoek op reservatiecode, klantnaam, e-mail of telefoon"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="reservation-workbench">
        <div className="list">
          {filteredReservations.length === 0 && (
            <div className="panel empty-state">Geen reservaties gevonden.</div>
          )}

          {filteredReservations.map((r) => (
            <button
              key={r.id}
              className={`list-row reservation-row${selectedReservation?.id === r.id ? " selected" : ""}`}
              onClick={() => setSelectedId(r.id)}
            >
              <div>
                <div className="row-title">{r.name}</div>
                <div className="row-meta">
                  {r.email} - {r.trailer?.name || `Trailer #${r.trailerId}`} - {formatDate(r.startDate)} tot {formatDate(r.endDate)}
                </div>
                <div className="status-line">
                  <span className={`badge ${reservationBadge(r.status)}`}>{statusLabels[r.status] || r.status}</span>
                  <span className={`badge ${depositBadge(r.depositStatus)}`}>Waarborg {depositLabels[r.depositStatus] || r.depositStatus}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <aside className="panel panel-pad detail-panel">
          {!selectedReservation ? (
            <div className="empty-state">Selecteer een reservatie om de details te bekijken.</div>
          ) : (
            <>
              <div className="detail-section">
                <h2 className="page-title" style={{ fontSize: 24 }}>
                  {selectedReservation.name}
                </h2>
                <p className="page-subtitle">{selectedReservation.email}</p>
                <div className="status-line">
                  <span className={`badge ${reservationBadge(selectedReservation.status)}`}>
                    Reservatie {statusLabels[selectedReservation.status] || selectedReservation.status}
                  </span>
                  <span className={`badge ${depositBadge(selectedReservation.depositStatus)}`}>
                    Waarborg {depositLabels[selectedReservation.depositStatus] || selectedReservation.depositStatus}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">Reservatie</h3>
                {selectedReservation.customerId && (
                  <Link className="button secondary customer-file-link" to={`/customers/${selectedReservation.customerId}`}>
                    Klantendossier openen
                  </Link>
                )}
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <label className="field"><span className="field-label">Voornaam</span><input className="input" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Naam</span><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></label>
                  <label className="field"><span className="field-label">E-mail</span><input className="input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />{editValidationErrors.email && <span className="field-error">{editValidationErrors.email}</span>}</label>
                  <label className="field"><span className="field-label">Telefoon</span><input className="input" type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />{editValidationErrors.phone && <span className="field-error">{editValidationErrors.phone}</span>}</label>
                  <label className="field">
                    <span className="field-label">Trailer</span>
                    <select className="select" value={editForm.trailerId} onChange={(e) => setEditForm({ ...editForm, trailerId: e.target.value })}>
                      <option value="">Selecteer trailer</option>
                      {trailers.map((trailer) => (
                        <option key={trailer.id} value={trailer.id}>{trailer.name} - MTM {trailer.mtm}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field"><span className="field-label">Startdatum</span><input className="input" type="date" value={editForm.startDate} onChange={(e) => updateEditStartDate(e.target.value)} /></label>
                  <label className="field"><span className="field-label">Einddatum</span><input className="input" type="date" min={editForm.startDate || undefined} value={editForm.endDate} onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })} /></label>
                </div>
                <button
                  className="button secondary"
                  onClick={saveEditReservation}
                >
                  Reservatiegegevens opslaan
                </button>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Trailer</div>
                    <div className="detail-value">
                      {selectedReservation.trailer?.name || `#${selectedReservation.trailerId}`}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Aangemaakt</div>
                    <div className="detail-value">{formatDateTime(selectedReservation.createdAt)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Reservatiecode</div>
                    <div className="detail-value">{selectedReservation.reservationCode || "-"}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Startdatum</div>
                    <div className="detail-value">{formatDate(selectedReservation.startDate)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Einddatum</div>
                    <div className="detail-value">{formatDate(selectedReservation.endDate)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Huurprijs</div>
                    <div className="detail-value">{formatMoney(selectedReservation.rentalPriceInfo?.rentalPrice)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Berekening</div>
                    <div className="detail-value">{selectedReservation.rentalPriceInfo?.explanation || "-"}</div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">Betaling en waarborg via Stripe</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Waarborgbedrag</div>
                    <div className="detail-value">EUR {selectedReservation.depositAmount || 0}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Waarborgstatus</div>
                    <div className="detail-value">{depositLabels[selectedReservation.depositStatus] || selectedReservation.depositStatus}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Vastgezet op</div>
                    <div className="detail-value">{formatDateTime(selectedReservation.depositHeldAt)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Terugbetaald op</div>
                    <div className="detail-value">{formatDateTime(selectedReservation.depositRefundedAt)}</div>
                  </div>
                </div>
                <div className="checkout-link-box" style={{ marginTop: 12 }}>
                  <div>
                    <div className="detail-label">Waarborgafhandeling</div>
                    <div className="row-meta">
                      {!selectedReservation.checkInAt && selectedReservation.status !== "COMPLETED"
                        ? "Waarborg: nog niet beschikbaar"
                        : selectedSettlement?.status === "SETTLED"
                          ? "Waarborg: afgehandeld"
                          : "Waarborg: nog af te handelen"}
                    </div>
                  </div>
                  {selectedSettlement ? (
                    <Link className="button secondary" to={`/admin/settlements/${selectedSettlement.id}`}>
                      Waarborg bekijken
                    </Link>
                  ) : (
                    <button
                      className="button secondary"
                      onClick={() => settlementMutation.mutate(selectedReservation.id)}
                      disabled={settlementMutation.isPending || (!selectedReservation.checkInAt && selectedReservation.status !== "COMPLETED")}
                    >
                      Waarborg afhandelen
                    </button>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">Operationele status</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Uitgecheckt</div>
                    <div className="detail-value">{formatDateTime(selectedReservation.checkOutAt)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Ingecheckt</div>
                    <div className="detail-value">{formatDateTime(selectedReservation.checkInAt)}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Schadekost</div>
                    <div className="detail-value">EUR {selectedReservation.damageCost || 0}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Schadenota</div>
                    <div className="detail-value">{selectedReservation.damageNote || "-"}</div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">Status aanpassen</h3>
                <div className="form-grid">
                  <select
                    className="select"
                    value={selectedReservation.status}
                    onChange={(e) => updateReservationStatus(e.target.value)}
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">Handmatige acties</h3>
                {selectedReservation && (
                  <div className="checkout-link-box">
                    <div>
                      <div className="detail-label">Check-out link</div>
                      <div className="row-meta">{checkoutLink}</div>
                    </div>
                    <div className="action-row">
                      <Link className="button secondary" to={`/checkout/${selectedReservation.id}`} target="_blank">
                        Open check-out link
                      </Link>
                      <button
                        className="button secondary"
                        onClick={() => {
                          navigator.clipboard?.writeText(checkoutLink);
                          toast.success("Check-out link gekopieerd");
                        }}
                      >
                        Kopieer link
                      </button>
                    </div>
                  </div>
                )}
                <div className="reservation-action-groups">
                  <div className="reservation-action-card">
                    <h4 className="reservation-action-title">Reservatie</h4>
                    <div className="action-row reservation-action-row">
                      <button
                        className="button"
                        onClick={() => updateReservationStatus("APPROVED")}
                        disabled={statusMutation.isPending}
                      >
                        Goedkeuren
                      </button>
                      <button
                        className="button danger"
                        onClick={() => updateReservationStatus("REJECTED")}
                        disabled={statusMutation.isPending}
                      >
                        Afkeuren
                      </button>
                    </div>
                  </div>

                  <div className="reservation-action-card">
                    <h4 className="reservation-action-title">Waarborg</h4>
                    <div className="action-row reservation-action-row">
                      <button
                        className="button secondary"
                        onClick={() => updateDepositStatus("PAID")}
                        disabled={depositMutation.isPending}
                      >
                        Waarborg betaald
                      </button>
                      <button
                        className="button secondary"
                        onClick={() => updateDepositStatus("UNPAID")}
                        disabled={depositMutation.isPending}
                      >
                        Waarborg niet betaald
                      </button>
                      <button
                        className="button secondary"
                        onClick={() => updateDepositStatus("REFUNDED")}
                        disabled={depositMutation.isPending}
                      >
                        Waarborg terugbetaald
                      </button>
                      <button
                        className="button secondary"
                        onClick={() => settlementMutation.mutate(selectedReservation.id)}
                        disabled={settlementMutation.isPending || (!selectedReservation.checkInAt && selectedReservation.status !== "COMPLETED")}
                      >
                        Waarborg afhandelen
                      </button>
                    </div>
                  </div>

                  <div className="reservation-action-card">
                    <h4 className="reservation-action-title">Documenten</h4>
                    <div className="action-row reservation-action-row">
                      <button
                        className="button secondary"
                        onClick={() => downloadDossierMutation.mutate(selectedReservation.id)}
                        disabled={downloadDossierMutation.isPending}
                      >
                        Download verhuurdossier
                      </button>
                    </div>
                  </div>

                  <div className="reservation-action-card danger-zone">
                    <h4 className="reservation-action-title">Gevaarlijke actie</h4>
                    <div className="action-row reservation-action-row">
                      <button
                        className="button danger"
                        onClick={() => deleteMutation.mutate(selectedReservation.id)}
                        disabled={deleteMutation.isPending}
                      >
                        Verwijderen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}
